import { type QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';
import { featuresGridConfig } from '@/features/features/grid/gridConfig';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { buildTagColorByName } from '@/features/shared/tags';
import { useTagOptions } from '@/shared/hooks';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { buildFeaturesColumns } from './featuresColumns';
import { useFeaturesTableRowActions } from './useFeaturesTableRowActions';
import type { FeaturesTableProps } from '@/features/features/components/FeaturesTable.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef } from '@/ui/primitives';
import type { FeatureDto, QueryResult } from '@flowqa/shared';

type FeaturesTableControllerInput = Pick<
  FeaturesTableProps,
  | 'fixedFilters'
  | 'initialFilterModel'
  | 'initialSortModel'
  | 'persistKey'
  | 'queryFn'
  | 'queryKey'
  | 'syncStateToUrl'
>;

type FeaturesTableController = {
  columns: Array<GridColDef<FeatureDto>>;
  deleteLoading: boolean;
  deleteTarget: ReturnType<typeof useFeaturesTableRowActions>['deleteTarget'];
  gridConfig: ReturnType<typeof featuresGridConfig>;
  isAuthenticated: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<FeatureDto>>;
  queryKey: QueryKey;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

export const useFeaturesTableController = ({
  fixedFilters,
  initialFilterModel,
  initialSortModel,
  persistKey,
  queryFn,
  queryKey,
  syncStateToUrl = false,
}: FeaturesTableControllerInput): FeaturesTableController => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useFeaturesTableRowActions({ selectedProjectId });
  const { tagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: isAuthenticated,
  });
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildFeaturesColumns({
        onProjectSelect: setSelectedProjectId,
        onDeleteRow,
        tagColorByName,
        deleteRowPendingId,
        onUpdateRowTagsColor,
        updateRowTagsColorPendingId,
      }),
    [
      deleteRowPendingId,
      onDeleteRow,
      onUpdateRowTagsColor,
      setSelectedProjectId,
      tagColorByName,
      updateRowTagsColorPendingId,
    ],
  );
  const gridConfig = featuresGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'features-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });

  return {
    columns,
    deleteLoading,
    deleteTarget,
    gridConfig,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: queryFn ?? gridConfig.queryFn,
    queryKey: queryKey ?? gridConfig.queryKey,
    selectedOrganisationId,
    selectedProjectId,
  };
};
